import type { LearningCatalog } from "../../types";
import { chatbotConfig } from "../config";
import type { ChatbotData, ChatbotResponse } from "../types";
import { logChatEvent } from "../telemetry/logChatEvent";
import { buildQueryMatchContext, detectIntent, parseChatQuery, shouldUseFallback } from "./query";
import { recommendResources } from "./recommend";
import { retrieveSnippets } from "./retrieve";

export function buildChatbotResponse(catalog: LearningCatalog, data: ChatbotData, userQuery: string): ChatbotResponse {
  const query = parseChatQuery(userQuery, data.topics, data.queryProfiles);
  const detectedIntent = detectIntent(query, data.intents);
  const matchContext = buildQueryMatchContext(query, data.topics, data.queryProfiles);
  const matchedTopics = matchContext.matchedTopics;
  const recommendationResult = recommendResources(
    catalog,
    matchContext,
    detectedIntent,
    chatbotConfig.maxExpandedRecommendations,
    data.queryProfiles
  );
  const snippetResult = retrieveSnippets(data.chunks, matchContext, chatbotConfig.maxSnippets, data.queryProfiles);
  const fallback = shouldUseFallback(matchContext);
  const useSnippets =
    !fallback &&
    chatbotConfig.allowExtractiveQa &&
    snippetResult.confidence !== "low" &&
    (query.normalized.startsWith("how") || query.normalized.startsWith("what") || snippetResult.confidence === "high");
  const confidence = fallback ? "low" : useSnippets ? snippetResult.confidence : ensureHelpfulRecallConfidence(recommendationResult.confidence);
  const mode = fallback ? "fallback" : useSnippets ? "api_free_extractive_qa" : "api_free_recommendation";
  const templateId = data.intents.find((intent) => intent.id === detectedIntent)?.responseTemplateId ?? "recommend_topic";
  const answerText =
    mode === "fallback"
      ? data.cannedResponses.fallback_low_confidence
      : useSnippets
        ? data.cannedResponses.extractive_answer_intro
        : data.cannedResponses[templateId] ?? data.cannedResponses.recommend_topic;
  const resources = selectRecommendationPool(
    recommendationResult.recommendations,
    confidence,
    isBroadQuery(query.normalized)
  );
  const response: ChatbotResponse = {
    id: `response-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    mode,
    answerText,
    resources: mode === "fallback" ? [] : resources,
    snippets: useSnippets && mode !== "fallback" ? snippetResult.snippets : [],
    citations:
      useSnippets && mode !== "fallback"
        ? snippetResult.snippets.map((snippet) => ({
            chunkId: snippet.chunk.id,
            resourceId: snippet.chunk.resourceId,
            resourceName: snippet.chunk.resourceName,
            resourceUrl: snippet.chunk.resourceUrl,
            heading: snippet.chunk.heading
          }))
        : [],
    followUps: buildFollowUps(matchedTopics, data),
    confidence,
    detectedIntent,
    matchedTopics
  };

  logChatEvent({
    eventType: mode === "fallback" ? "chatbot_unresolved_query" : "chatbot_query",
    query: userQuery,
    detectedIntent,
    matchedTopics,
    responseMode: mode,
    confidence
  });

  return response;
}

function ensureHelpfulRecallConfidence(confidence: ChatbotResponse["confidence"]): ChatbotResponse["confidence"] {
  return confidence === "low" ? "medium" : confidence;
}

function selectRecommendationPool<T extends { score: number }>(recommendations: T[], confidence: string, broadQuery: boolean): T[] {
  if (confidence === "low") {
    return [];
  }

  if (confidence === "high" && broadQuery) {
    const topScore = recommendations[0]?.score ?? 0;
    return recommendations.filter((recommendation) => recommendation.score >= topScore * 0.62).slice(0, chatbotConfig.maxExpandedRecommendations);
  }

  return recommendations.slice(0, chatbotConfig.maxRecommendations);
}

function isBroadQuery(normalizedQuery: string): boolean {
  return ["resources", "resource", "show me", "find", "list", "options", "examples"].some((term) => normalizedQuery.includes(term));
}

function buildFollowUps(matchedTopics: string[], data: ChatbotData): string[] {
  const topicLabels = matchedTopics
    .map((topicId) => data.topics.find((topic) => topic.id === topicId)?.label)
    .filter(Boolean)
    .slice(0, 2);

  if (topicLabels.length > 0) {
    return [`Show more on ${topicLabels[0]}`, "Find a quick resource", "Get guidance"];
  }

  return ["Get guidance", "Customer discovery", "Pricing and revenue"];
}
